import { useEffect, useMemo, useState, type ReactNode } from "react";
import { generatePath, useLocation } from "react-router";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { CalendarDays } from "@acto/web-common/components";
import type Meeting from "@acto/taskpane/core/domain/Meeting";
import { useMeetings, UNLIMITED_MEETINGS } from "@acto/taskpane/core/data-access/meetings/useMeetings";
import {
  CalendarGrid,
  CalendarHeader,
  type CalendarSelectOption,
  todayUtc,
  toIsoDate,
  useCalendarMonth,
  useCalendarWeek,
} from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import {
  MeetingRow,
  MeetingRowAddress,
  MeetingRowInfo,
  MeetingRowName,
  MeetingSectionLabel,
  MeetingTimeBox,
  formatMeetingRowAddress,
} from "@acto/taskpane/field-sales-2/components/primitives/MeetingRow";
import { NextMeetingCard } from "@acto/taskpane/field-sales-2/components/patterns/NextMeetingCard";
import { pickNextMeeting } from "@acto/taskpane/field-sales-2/utils/meetingFocus";
import { useNow } from "@acto/taskpane/field-sales-2/utils/hooks/useNow";
import { useRoutePlanningLightEnabled } from "@acto/taskpane/field-sales-2/data-access/useRoutePlanningLightEnabled";
import { OutlinedPillButton } from "@acto/taskpane/field-sales-2/components/primitives/OutlinedPillButton";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { formatHourMinute } from "@acto/taskpane/field-sales-2/utils/format";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { AnalyticsEvents, useTrack } from "@acto/taskpane/core/analytics";
import { useContributeChatScope, meetingsToPlannedMeetings } from "@acto/taskpane/core/chat-envelope";
import { visibleRange } from "./visibleRange";

const Section = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  margin: `0 ${theme.fieldSalesTwo.spacing[12]}`,
  padding: theme.fieldSalesTwo.spacing[24],
  borderRadius: theme.fieldSalesTwo.radius["38"],
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
}));

const Meetings = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  paddingTop: theme.fieldSalesTwo.spacing[16],
}));

const EmptyState = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
  minHeight: 80,
  marginTop: theme.fieldSalesTwo.spacing[16],
  padding: theme.fieldSalesTwo.spacing[20],
  boxSizing: "border-box",
  border: `${theme.strokeWidth.thin} dashed ${theme.fieldSalesTwo.colors.neutral[300]}`,
  borderRadius: theme.fieldSalesTwo.radius[24],
  textAlign: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const AddMeetingButton = styled(OutlinedPillButton)(({ theme }) => ({
  alignSelf: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  paddingInline: theme.fieldSalesTwo.spacing[20],
}));

export type CalendarView = "week" | "month";

// CalendarSelectMenu keys options by a numeric `value`; map the two views to stable indices.
const VIEW_INDEX: Record<CalendarView, number> = { week: 0, month: 1 };
const VIEW_BY_INDEX: CalendarView[] = ["week", "month"];

interface MeetingCalendarProps {
  /** Initial/active view. Defaults to "week" (Home's behavior). */
  view?: CalendarView;
  /** Render the Wöchentlich/Monatlich dropdown. Off by default (Home has no toggle). */
  showViewToggle?: boolean;
  /** When provided, the component is controlled: the parent owns `view` and receives changes. */
  onViewChange?: (view: CalendarView) => void;
  /** Notified whenever the selected day changes (and on mount). Lets a parent seed flows — e.g.
   * the Calendar page pre-fills the plan-meeting sheet with the selected day. Pass a stable
   * callback (e.g. a `useState` setter) to avoid re-firing every render. */
  onSelectedDateChange?: (date: Date | null) => void;
  /** Notified with the selected day's meetings (and on mount), same stability contract as
   * `onSelectedDateChange`. */
  onDayMeetingsChange?: (meetings: Meeting[]) => void;
  /** Rendered under the meeting list, only when the selected day has appointments. */
  dayFooter?: ReactNode;
  /** When provided, the empty state renders an add-meeting CTA; invoked with the currently
   * selected day (null when none). */
  onAddMeeting?: (selectedDate: Date | null) => void;
  /** Which ambient-context scope the selected day's meetings belong to. */
  contributeTo?: "home" | "calendar";
}

export function MeetingCalendar({
  view = "week",
  showViewToggle = false,
  onViewChange,
  onSelectedDateChange,
  onDayMeetingsChange,
  dayFooter,
  onAddMeeting,
  contributeTo = "home",
}: MeetingCalendarProps) {
  const t = useTranslations("fieldSalesTwo.home.calendar");
  const navigate = useDirectionalNavigate();
  const location = useLocation();
  const track = useTrack();

  // Uncontrolled fallback when the parent doesn't own `view`.
  const [internalView, setInternalView] = useState<CalendarView>(view);
  const activeView = onViewChange ? view : internalView;

  // Both hooks run every render (Rules of Hooks); only the active controller is consumed.
  const weekCal = useCalendarWeek({ defaultSelectedDate: todayUtc() });
  const monthCal = useCalendarMonth({ defaultSelectedDate: todayUtc() });
  const calendar = activeView === "month" ? monthCal : weekCal;

  // The two controllers hold independent state; carry the active selection into the incoming one on
  // toggle so navigating away then switching view doesn't snap the user back to today.
  const setView = (next: CalendarView) => {
    if (next !== activeView) {
      const outgoing = activeView === "month" ? monthCal : weekCal;
      const incoming = next === "month" ? monthCal : weekCal;
      if (outgoing.selectedDate) incoming.goToDate(outgoing.selectedDate);
    }
    if (onViewChange) onViewChange(next);
    else setInternalView(next);
  };

  // Fetch the visible span (week or whole month grid) and expand recurring series within it.
  const range = useMemo(() => visibleRange(calendar), [calendar]);
  const { data: meetings } = useMeetings({
    dateRange: range,
    limit: UNLIMITED_MEETINGS,
    includeAddress: true,
  });

  const meetingDays = useMemo(
    () => new Set((meetings ?? []).map((m) => m.scheduledDate.format("YYYY-MM-DD"))),
    [meetings],
  );

  const todayIso = toIsoDate(todayUtc());
  const weeks = useMemo(
    () =>
      calendar.weeks.map((week) =>
        week.map((day) => {
          const iso = toIsoDate(day.date);
          return { ...day, hasMeeting: meetingDays.has(iso), isToday: iso === todayIso };
        }),
      ),
    [calendar.weeks, meetingDays, todayIso],
  );

  const selectedIso = calendar.selectedDate ? toIsoDate(calendar.selectedDate) : null;

  // Surface the selected day to a controlled parent (and on mount). Keyed on the ISO string, not
  // the Date object: the Date gets a new identity every render, so depending on it would re-fire
  // (and loop once the parent stores it in state) — the string only changes when the day changes.
  useEffect(() => {
    onSelectedDateChange?.(calendar.selectedDate ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedIso is the stable proxy for calendar.selectedDate
  }, [selectedIso, onSelectedDateChange]);
  const dayMeetings = useMemo(
    () =>
      (meetings ?? [])
        .filter((m) => selectedIso != null && m.scheduledDate.format("YYYY-MM-DD") === selectedIso)
        .sort((a, b) => (a.scheduledStartTime ?? "").localeCompare(b.scheduledStartTime ?? "")),
    [meetings, selectedIso],
  );

  useEffect(() => {
    onDayMeetingsChange?.(dayMeetings);
  }, [dayMeetings, onDayMeetingsChange]);

  // Gating the pick rather than the two render sites is what keeps the card and the list in
  // step: a null pick both hides the card and turns the filter below into a no-op, so the
  // appointment cannot fall out of one without returning to the other.
  const nextMeetingEnabled = useRoutePlanningLightEnabled();
  const now = useNow();
  const nextMeeting = useMemo(
    () => (nextMeetingEnabled ? pickNextMeeting(dayMeetings, now) : null),
    [nextMeetingEnabled, dayMeetings, now],
  );

  // The card and the list are mutually exclusive. Only the rendering narrows: `dayMeetings`
  // stays whole, since what this component publishes is the day and not the list.
  const listedMeetings = useMemo(
    () => dayMeetings.filter((meeting) => meeting.id !== nextMeeting?.id),
    [dayMeetings, nextMeeting],
  );

  // Ambient context: contribute the selected day and its meetings (plus the active view on calendar).
  const chatFragment = useMemo(() => {
    const base = { selected_date: selectedIso ?? undefined, planned_meetings: meetingsToPlannedMeetings(dayMeetings) };
    return contributeTo === "calendar" ? { ...base, view: activeView } : base;
  }, [selectedIso, dayMeetings, contributeTo, activeView]);
  useContributeChatScope(contributeTo, chatFragment);

  const openCustomer = (customerId: string) => {
    navigate(generatePath(ActoRoutes.CUSTOMER_DETAILS, { id: encodeURIComponent(customerId) }), {
      state: { from: location.pathname },
    });
  };

  const viewOptions: CalendarSelectOption[] = [
    { value: VIEW_INDEX.week, label: t("viewWeek") },
    { value: VIEW_INDEX.month, label: t("viewMonth") },
  ];

  return (
    <Section>
      <CalendarHeader
        title={calendar.title}
        previousLabel={activeView === "month" ? t("previousMonth") : t("previousWeek")}
        nextLabel={activeView === "month" ? t("nextMonth") : t("nextWeek")}
        onPrevious={calendar.goToPrevious}
        onNext={calendar.goToNext}
        viewDropdown={
          showViewToggle
            ? {
                options: viewOptions,
                currentIndex: VIEW_INDEX[activeView],
                onSelect: (next) => setView(VIEW_BY_INDEX[next] ?? "week"),
                triggerLabel: t("viewToggleLabel"),
              }
            : undefined
        }
      />
      <Divider />
      <CalendarGrid weekdayLabels={calendar.weekdayLabels} weeks={weeks} onSelectDay={calendar.selectDay} />
      {dayMeetings.length === 0 && (
        <EmptyState>
          <span>{t("noMeetings")}</span>
          {onAddMeeting && (
            <AddMeetingButton type="button" onClick={() => onAddMeeting(calendar.selectedDate ?? null)}>
              <CalendarDays aria-hidden="true" />
              {t("addMeeting")}
            </AddMeetingButton>
          )}
        </EmptyState>
      )}
      {listedMeetings.length > 0 && (
        <Meetings>
          <MeetingSectionLabel>{t("plannedMeetings")}</MeetingSectionLabel>
          {listedMeetings.map((meeting) => {
            const address = formatMeetingRowAddress(meeting);
            return (
              <MeetingRow
                key={meeting.id}
                type="button"
                onClick={() => {
                  track(AnalyticsEvents.APPOINTMENT_OPENED, {
                    appointmentId: meeting.id,
                    customerId: meeting.customer.id,
                    customerName: meeting.customer.name,
                    scheduledDate: meeting.scheduledDate.format("YYYY-MM-DD"),
                  });
                  openCustomer(meeting.customer.id);
                }}
              >
                <MeetingTimeBox>
                  {meeting.scheduledStartTime ? formatHourMinute(meeting.scheduledStartTime) : ""}
                </MeetingTimeBox>
                <MeetingRowInfo>
                  <MeetingRowName>{meeting.customer.name}</MeetingRowName>
                  {address && <MeetingRowAddress>{address}</MeetingRowAddress>}
                </MeetingRowInfo>
              </MeetingRow>
            );
          })}
        </Meetings>
      )}
      {nextMeeting && (
        <NextMeetingCard
          meeting={nextMeeting}
          onOpenCustomer={(customerId) => {
            track(AnalyticsEvents.APPOINTMENT_OPENED, {
              appointmentId: nextMeeting.id,
              customerId,
              customerName: nextMeeting.customer.name,
              scheduledDate: nextMeeting.scheduledDate.format("YYYY-MM-DD"),
            });
            openCustomer(customerId);
          }}
          onOpenMaps={() =>
            track(AnalyticsEvents.NEXT_MEETING_MAPS_OPENED, {
              appointmentId: nextMeeting.id,
              customerId: nextMeeting.customer.id,
              customerName: nextMeeting.customer.name,
            })
          }
        />
      )}
      {dayMeetings.length > 0 && dayFooter}
    </Section>
  );
}
